import { Link, useLocation, useRouter } from '@tanstack/react-router';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { AuthProvider } from '../../auth/AuthProvider';
import {
  hasPendingGoogleRedirect,
  REDIRECT_STORAGE_KEY,
  sanitizeRedirect,
} from '../../auth/auth-storage';
import { useAuth } from '../../auth/hooks';
import { DEFAULT_SHUTDOWN_STATUS, useShutdownStatus } from '../../shutdown/api';

type FaqItem = {
  question: string;
  answer: string;
};

const heroHandsCutout = '/generated/landing/cutouts/hands-cutout.webp';
const heroSphereCutout = '/generated/landing/cutouts/sphere-cutout.webp';

const GoogleIcon = () => (
  <svg
    className="h-5 w-5"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const MatrixRain = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const fontSize = 15;
    const chars = '01<>[]{}+/\\';
    let animation = 0;
    let columns = 0;
    let drops: number[] = [];
    let startTimer = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      columns = Math.max(1, Math.floor(canvas.width / fontSize));
      drops = Array(columns).fill(1);
    };

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(5, 150, 105, 0.16)';
      ctx.font = `${fontSize}px monospace`;

      for (let index = 0; index < drops.length; index += 1) {
        const char = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillText(char, index * fontSize, drops[index] * fontSize);
        if (drops[index] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[index] = 0;
        }
        drops[index] += 1;
      }

      animation = window.requestAnimationFrame(draw);
    };

    const start = () => {
      resize();
      draw();
    };

    startTimer = window.setTimeout(start, 180);
    window.addEventListener('resize', resize);

    return () => {
      window.clearTimeout(startTimer);
      window.cancelAnimationFrame(animation);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="pointer-events-none fixed inset-0 z-0 opacity-45" />;
};

const HeroEnergyScene = ({ imageAlt }: { imageAlt: string }) => (
  <div className="pointer-events-none relative overflow-visible px-2 pb-8 pt-8 md:px-6 md:pb-10 md:pt-10">
    <div className="relative mx-auto h-[460px] max-w-[760px] overflow-visible isolate md:h-[720px]">
      <div className="hero-reveal absolute inset-[10%_10%] z-0 bg-[radial-gradient(circle_at_50%_48%,rgba(255,255,255,0.08),transparent_34%)]" />

      <div className="hero-orbit-line absolute inset-[13%_14%] z-20 rounded-full border border-white/20" />
      <div className="hero-orbit-line absolute inset-[22%_20%] z-20 rounded-full border border-white/12" />
      <div className="hero-orbit-line absolute inset-[30%_28%] z-20 rounded-full border border-matrix-green/18" />

      {[
        'left-[20%] top-[29%]',
        'left-[28%] top-[21%]',
        'right-[20%] top-[31%]',
        'right-[25%] top-[58%]',
        'left-[49%] top-[14%]',
        'left-[50%] top-[76%]',
        'left-[17%] top-[43%]',
        'right-[16%] top-[47%]',
      ].map((position, index) => (
        <span
          key={`${position}-${index}`}
          className={cn(
            'hero-spark absolute h-2 w-2 rounded-full bg-matrix-green/80 shadow-[0_0_18px_rgba(5,150,105,0.75)]',
            position
          )}
        />
      ))}

      <div className="hero-sphere-core absolute inset-[25%_25%_25%_25%] z-30 md:inset-[16%_24%_16%_24%]">
        <div className="hero-sphere-glow absolute inset-[-34%] rounded-full bg-[radial-gradient(circle,rgba(5,150,105,0.78),rgba(5,150,105,0.3)_38%,transparent_72%)] blur-3xl" />
        <img
          src={heroSphereCutout}
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full scale-[2.05] object-contain object-center mix-blend-screen brightness-[1.12] contrast-[1.18] md:scale-[2.25]"
        />
        <div className="hero-sphere-ring absolute inset-[-14%] rounded-full border border-matrix-green/45" />
        <div className="hero-sphere-ring absolute inset-[-28%] rounded-full border border-white/12" />
        <div className="hero-sphere-ring absolute inset-[-42%] rounded-full border border-matrix-green/16" />
      </div>

      <div className="hero-hands-layer absolute inset-[-4%_-12%_-8%_-12%] z-40 md:inset-[-8%_-18%_-12%_-18%]">
        <div
          className="hero-hand-inner-left absolute inset-0"
          style={{
            clipPath: 'inset(0 50% 0 0)',
            transformOrigin: '30% 56%',
          }}
        >
          <img
            src={heroHandsCutout}
            alt={imageAlt}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="h-full w-full object-contain object-center drop-shadow-[0_0_36px_rgba(5,150,105,0.18)]"
          />
        </div>

        <div
          className="hero-hand-inner-right absolute inset-0"
          style={{
            clipPath: 'inset(0 0 0 50%)',
            transformOrigin: '70% 56%',
          }}
        >
          <img
            src={heroHandsCutout}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="h-full w-full object-contain object-center drop-shadow-[0_0_36px_rgba(5,150,105,0.18)]"
          />
        </div>
      </div>
    </div>
  </div>
);

const LandingPageContent = () => {
  const { t, i18n } = useTranslation(['marketing', 'auth']);
  const router = useRouter();
  const location = useLocation();
  const { status, isLoading, signInWithGoogle, error, errorCode } = useAuth();
  const { data: shutdownStatus = DEFAULT_SHUTDOWN_STATUS } = useShutdownStatus();
  const isRTL = i18n.language === 'ar';
  const pageRef = useRef<HTMLDivElement>(null);
  const heroVisualRef = useRef<HTMLDivElement>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [postAuthRedirect, setPostAuthRedirect] = useState<string | null>(null);
  const [isReturningFromGoogleRedirect, setIsReturningFromGoogleRedirect] = useState(() =>
    hasPendingGoogleRedirect()
  );
  const faqItems = t('faq.questions', { returnObjects: true }) as unknown as FaqItem[];
  const searchRedirect = useMemo(() => {
    const search = location.search as Record<string, unknown>;
    return typeof search.redirect === 'string' ? sanitizeRedirect(search.redirect) : undefined;
  }, [location.search]);
  const storedRedirect =
    typeof window !== 'undefined'
      ? sanitizeRedirect(window.localStorage.getItem(REDIRECT_STORAGE_KEY))
      : undefined;
  const redirectParam = searchRedirect ?? storedRedirect ?? '/studio';
  const isCompletingGoogleSignIn = isReturningFromGoogleRedirect && status === 'loading';
  const isBusy = isLoading || isGoogleLoading || isCompletingGoogleSignIn;
  const registrationsClosed = shutdownStatus.controls.registrations;
  const registrationAttemptBlocked = errorCode === 'SHUTDOWN_REGISTRATIONS_DISABLED';

  useEffect(() => {
    setIsReturningFromGoogleRedirect(hasPendingGoogleRedirect());
  }, [status, isLoading, error]);

  useEffect(() => {
    const target = postAuthRedirect ?? searchRedirect ?? storedRedirect;
    if (status !== 'authenticated' || !target) {
      return;
    }

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(REDIRECT_STORAGE_KEY);
    }
    void router.navigate({ to: target as never, replace: true });
  }, [postAuthRedirect, router, searchRedirect, status, storedRedirect]);

  const handleGoogleSignIn = async () => {
    setFormError(null);
    setPostAuthRedirect(redirectParam);
    try {
      setIsGoogleLoading(true);
      await signInWithGoogle({ redirect: redirectParam });
    } catch (googleError) {
      setPostAuthRedirect(null);
      setFormError(
        googleError instanceof Error
          ? googleError.message
          : t('messages.googleFailed', { ns: 'auth' })
      );
    } finally {
      setIsGoogleLoading(false);
    }
  };

  useEffect(() => {
    const root = pageRef.current;
    const heroVisual = heroVisualRef.current;
    if (!root || !heroVisual) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let active = true;
    let cleanupPointerListeners: (() => void) | undefined;
    let cleanupAnimations: (() => void) | undefined;

    void (async () => {
      const { default: gsap } = await import('gsap');
      if (!active) {
        return;
      }

      const ctx = gsap.context(() => {
        if (!reduceMotion) {
          gsap.from('.hero-reveal', {
            opacity: 0,
            y: 28,
            duration: 0.85,
            stagger: 0.08,
            ease: 'power3.out',
          });

          gsap.fromTo(
            '.hero-hand-inner-left',
            {
              rotate: -50,
              xPercent: -4,
              yPercent: 8,
            },
            {
              rotate: -40,
              xPercent: 3,
              yPercent: -4,
              duration: 4.8,
              repeat: -1,
              yoyo: true,
              ease: 'sine.inOut',
            }
          );

          gsap.fromTo(
            '.hero-hand-inner-right',
            {
              rotate: 50,
              xPercent: 4,
              yPercent: 8,
            },
            {
              rotate: 40,
              xPercent: -3,
              yPercent: -4,
              duration: 5.1,
              repeat: -1,
              yoyo: true,
              ease: 'sine.inOut',
            }
          );

          gsap.to('.hero-hands-layer', {
            yPercent: 2.5,
            xPercent: -1.5,
            duration: 6.4,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
          });

          gsap.to('.hero-orbit-line', {
            rotate: (index) => (index % 2 === 0 ? 10 : -12),
            scale: (index) => (index === 1 ? 1.04 : 1.08),
            duration: (index) => 7.2 + index * 1.4,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
          });

          gsap.to('.hero-sphere-core', {
            scale: 1.14,
            rotate: 10,
            duration: 2.3,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
          });

          gsap.to('.hero-sphere-glow', {
            opacity: 0.84,
            scale: 1.32,
            duration: 2,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
          });

          gsap.to('.hero-spark', {
            y: (index) => (index % 2 === 0 ? -18 : 16),
            x: (index) => (index % 2 === 0 ? 8 : -6),
            opacity: 0.35,
            duration: (index) => 1.8 + index * 0.18,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            stagger: 0.04,
          });

          gsap.to('.hero-sphere-ring', {
            rotate: (index) => (index === 0 ? 8 : -10),
            scale: (index) => (index === 0 ? 1.03 : 1.08),
            duration: (index) => (index === 0 ? 4.2 : 5.4),
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
          });

          const rotateXTo = gsap.quickTo(heroVisual, 'rotationX', {
            duration: 0.45,
            ease: 'power3.out',
          });
          const rotateYTo = gsap.quickTo(heroVisual, 'rotationY', {
            duration: 0.45,
            ease: 'power3.out',
          });

          const handlePointerMove = (event: PointerEvent) => {
            const bounds = heroVisual.getBoundingClientRect();
            const offsetX = (event.clientX - bounds.left) / bounds.width - 0.5;
            const offsetY = (event.clientY - bounds.top) / bounds.height - 0.5;
            rotateYTo(offsetX * (isRTL ? -8 : 8));
            rotateXTo(offsetY * -8);
          };

          const handlePointerLeave = () => {
            rotateXTo(0);
            rotateYTo(0);
          };

          heroVisual.addEventListener('pointermove', handlePointerMove);
          heroVisual.addEventListener('pointerleave', handlePointerLeave);
          cleanupPointerListeners = () => {
            heroVisual.removeEventListener('pointermove', handlePointerMove);
            heroVisual.removeEventListener('pointerleave', handlePointerLeave);
          };
        }
      }, root);

      cleanupAnimations = () => {
        cleanupPointerListeners?.();
        ctx.revert();
      };
    })();

    return () => {
      active = false;
      cleanupAnimations?.();
    };
  }, [isRTL]);

  return (
    <div
      ref={pageRef}
      className="dark relative min-h-screen overflow-x-hidden bg-background text-foreground"
    >
      <MatrixRain />
      <div className="pointer-events-none fixed inset-0 z-0 bg-grid-white/5 opacity-45" />
      <div className="pointer-events-none fixed inset-0 z-0 bg-scanlines/8 opacity-35" />
      <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.05),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.07),transparent_42%)]" />

      <header className="relative z-20 border-b bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-5">
          <Link to="/" className="flex items-center gap-3">
            <img src="/masfufah-mark-white.svg" alt="Masfufah" className="h-12 w-12" />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher triggerClassName="bg-background/80" />
            <Button type="button" size="sm" onClick={handleGoogleSignIn} disabled={isBusy}>
              {isBusy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <GoogleIcon />}
              <span className="ms-2 hidden sm:inline">
                {t('form.googleButton', { ns: 'auth' })}
              </span>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10">
        <section className="relative z-0 flex min-h-[calc(100vh-88px)] items-center px-4 py-10 md:min-h-[calc(100vh-96px)] md:py-16">
          <div className="mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[0.84fr,1.16fr] lg:items-center">
            <div className="space-y-7">
              <h1
                className={cn(
                  'hero-reveal whitespace-pre-line text-5xl font-semibold leading-[0.92] tracking-tight text-foreground md:text-7xl',
                  isRTL && 'font-arabic'
                )}
              >
                {t('landing.heroTitle')}
              </h1>
              <p className="hero-reveal max-w-md text-base text-muted-foreground md:text-xl">
                {t('landing.heroSubtitle')}
              </p>
              <div className="hero-reveal flex max-w-sm flex-col gap-3">
                {registrationsClosed ? (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm leading-6">
                    <p className="font-semibold text-foreground">
                      {t('landing.registrationClosed.title')}
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      {t('landing.registrationClosed.body')}
                    </p>
                    <Link
                      to="/closure"
                      className="mt-2 inline-flex font-medium text-foreground underline underline-offset-4"
                    >
                      {t('landing.registrationClosed.link')}
                    </Link>
                  </div>
                ) : null}
                <Button
                  type="button"
                  size="lg"
                  className="h-12 px-7"
                  onClick={handleGoogleSignIn}
                  disabled={isBusy}
                >
                  {isBusy ? <Loader2 className="me-3 h-5 w-5 animate-spin" /> : <GoogleIcon />}
                  <span className="ms-3 text-base font-semibold">
                    {isCompletingGoogleSignIn
                      ? t('messages.finishingGoogleTitle', { ns: 'auth' })
                      : t('form.googleButton', { ns: 'auth' })}
                  </span>
                </Button>
                {formError || error ? (
                  <p role="alert" className="text-sm leading-6 text-destructive">
                    {registrationAttemptBlocked
                      ? t('landing.registrationClosed.attemptBlocked')
                      : (formError ?? error)}
                  </p>
                ) : null}
              </div>
            </div>

            <div ref={heroVisualRef} className="[perspective:1800px] [transform-style:preserve-3d]">
              <HeroEnergyScene imageAlt={t('landing.heroImageAlt')} />
            </div>
          </div>
        </section>

        <section className="relative z-10 px-4 pb-24 pt-6 md:px-6 md:pb-28">
          <div className="mx-auto grid w-full max-w-7xl gap-8 lg:grid-cols-[0.86fr,1.14fr] lg:items-start">
            <div className="space-y-5 lg:sticky lg:top-24">
              <div className="space-y-4">
                <h2
                  className={cn(
                    'max-w-lg text-3xl font-semibold leading-tight text-foreground md:text-5xl',
                    isRTL && 'font-arabic'
                  )}
                >
                  {t('faq.title')}
                </h2>
              </div>
            </div>

            <div className="space-y-4">
              {faqItems.map((item, index) => {
                const isOpen = openFaqIndex === index;

                return (
                  <article
                    key={item.question}
                    className={cn(
                      'relative z-10 overflow-hidden rounded-lg border transition-colors duration-200',
                      isOpen
                        ? 'bg-accent text-accent-foreground shadow-sm'
                        : 'bg-card hover:bg-accent/60'
                    )}
                  >
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() =>
                        setOpenFaqIndex((current) => (current === index ? null : index))
                      }
                      className="relative z-10 flex w-full items-start gap-4 px-5 py-5 text-start md:px-7 md:py-6"
                    >
                      <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-md border bg-muted text-sm font-semibold text-muted-foreground">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1">
                        <span
                          className={cn(
                            'block text-lg font-medium leading-8 text-foreground md:text-xl',
                            isRTL && 'font-arabic'
                          )}
                        >
                          {item.question}
                        </span>
                      </span>
                      <ChevronDown
                        className={cn(
                          'mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300',
                          isOpen && 'rotate-180'
                        )}
                      />
                    </button>

                    <div
                      className={cn(
                        'overflow-hidden px-5 transition-all duration-300 ease-out md:px-7',
                        isOpen ? 'max-h-[28rem] pb-6 opacity-100' : 'max-h-0 pb-0 opacity-0'
                      )}
                    >
                      <p
                        className={cn(
                          'border-t pt-4 text-sm leading-7 text-muted-foreground md:text-base',
                          isRTL && 'font-arabic'
                        )}
                      >
                        {item.answer}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t bg-background/80 py-12 backdrop-blur-xl">
        <div className="mx-auto w-full max-w-7xl px-6">
          <div className="mb-8 flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex flex-col items-center gap-3 md:items-start">
              <img src="/masfufah-mark-white.svg" alt="Masfufah" className="h-10 w-10" />
              <p className="text-center text-xs text-muted-foreground md:text-left">
                {t('footer.entity')}
              </p>
            </div>
            <div className="flex items-center gap-6">
              <Link
                to="/terms"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {t('footer.terms')}
              </Link>
              <Link
                to="/privacy"
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {t('footer.privacy')}
              </Link>
            </div>
          </div>
          <div className="border-t pt-6 text-center">
            <p className="text-xs text-muted-foreground">
              {t('footer.copy', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export const LandingPage = () => (
  <AuthProvider>
    <LandingPageContent />
  </AuthProvider>
);
